import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { SmartModalService } from '@certemy/ui-components';
import { ComputedFrom, required, withMessage } from '@certemy/common';
import _uniq from 'lodash-es/uniq';
import { getMergedFormErrors } from 'utils/helpers';
import { catchError, filter, take } from 'rxjs/operators';
import _get from 'lodash-es/get';
import { OrganizationRolesService } from 'features/organization-settings/organization-roles/organization-roles.service';
import { NEVER, Observable, of } from 'rxjs';
import { ORGANIZATION_ROLES_MESSAGE } from 'features/organization-settings/organization-roles/organization-roles.constants';

@Component({
  selector: 'cm-organization-roles-modal',
  templateUrl: './organization-roles-modal.html',
  styleUrls: ['./organization-roles-modal.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrganizationRolesModalComponent implements OnInit {
  @Input() row;

  isNew = false;
  form: UntypedFormGroup;
  showErrors = false;
  validationMessages: string[];

  constructor(
    private ngbActiveModal: NgbActiveModal,
    private fb: UntypedFormBuilder,
    private smartModalService: SmartModalService,
    private organizationRolesService: OrganizationRolesService,
    private cd: ChangeDetectorRef,
  ) {}

  ngOnInit() {
    this.isNew = !this.row;
    this.initForm();
  }

  private initForm() {
    this.form = this.fb.group({
      name: [null, [withMessage(ORGANIZATION_ROLES_MESSAGE.REQUIRED_FIELD, required)]],
    });
    if (!this.isNew) {
      this.form.patchValue(this.row);
    }
  }

  onModalClose() {
    if (this.form.dirty) {
      return this.smartModalService
        .openUnsavedChanges()
        .pipe(filter(confirm => !!confirm))
        .subscribe(() => this.modalDissmiss());
    }
    this.modalDissmiss();
  }

  private modalDissmiss() {
    this.ngbActiveModal.dismiss();
  }

  @ComputedFrom('form')
  get nameFormControl() {
    return this.form.get('name');
  }

  submitForm() {
    this.showErrors = this.form.invalid;
    this.validationMessages = [];
    if (this.showErrors) {
      return this.setValidationMessages();
    }
    const payload = this.form.value;
    this.rowTransform$(payload)
      .pipe(
        catchError(err => {
          const text = _get(err, 'body.error_message');
          if (text.toLowerCase().includes('already exists')) {
            this.showErrors = true;
            this.nameFormControl.setErrors({ required: ORGANIZATION_ROLES_MESSAGE.ROLE_ALREADY_EXIST });
            this.setValidationMessages();
            this.cd.markForCheck();
            return NEVER;
          }
        }),
        take(1),
      )
      .subscribe(resp => {
        this.ngbActiveModal.close({
          ...this.row,
          ...resp,
        });
      });
  }

  private rowTransform$(payload): Observable<any> {
    if (this.isNew) {
      return this.organizationRolesService.createOrganizationRole(payload);
    }
    return this.organizationRolesService.updateOrganizationRole(this.row.organizationRoleId, payload);
  }

  private setValidationMessages() {
    this.validationMessages = _uniq(
      getMergedFormErrors(this.form)
        .map(errors => Object.values(errors).find(el => typeof el === 'string'))
        .filter(hasValidationMessage => !!hasValidationMessage),
    );
  }
}
